<!-- AI CES live books (ETH 15m + ETH 5m) — wanted ON/OFF, equity, last tick. -->
<script>
  import { onMount, createEventDispatcher } from 'svelte'
  import { getCesLive, setCesLiveEnabled } from '../api.js'
  import { OPS_ERROR_RU } from '../lib/format.js'

  const dispatch = createEventDispatcher()

  let markets = []
  let loading = true
  let busy = ''
  let err = ''
  let msg = ''
  let mutexBanner = ''

  async function refresh() {
    err = ''
    mutexBanner = ''
    try {
      const { res, data } = await getCesLive()
      if (res?.ok && data?.ok && Array.isArray(data.markets)) {
        markets = data.markets
      } else {
        err = (data && data.error) || 'ces_status_failed'
      }
    } catch {
      err = 'ces_status_failed'
    } finally {
      loading = false
    }
  }

  /**
   * @param {string} market
   * @param {boolean} next
   */
  async function onToggle(market, next) {
    if (busy) return
    busy = market
    err = ''
    msg = ''
    const prev = markets.map((m) => ({ ...m }))
    markets = markets.map((m) =>
      m.market_key === market ? { ...m, armed: next, enabled_wanted: next } : m,
    )
    try {
      const r = await setCesLiveEnabled(market, next)
      if (!r.ok) {
        markets = prev
        const code = r.error || 'ces_toggle_failed'
        err = r.error_ru || OPS_ERROR_RU[code] || code
        return
      }
      if (r.data && r.data.market) {
        markets = markets.map((m) =>
          m.market_key === market ? { ...m, ...r.data.market } : m,
        )
      } else {
        await refresh()
      }
      msg = next ? 'CES wanted ON' : 'CES wanted OFF'
      dispatch('changed', { market, enabled: next })
    } catch {
      markets = prev
      err = 'Сбой запроса'
    } finally {
      busy = ''
    }
  }

  function equityTxt(m) {
    const n = Number(m && m.equity)
    if (m == null || m.equity == null || !Number.isFinite(n)) return '—'
    return `$${n.toFixed(2)}`
  }

  onMount(() => {
    refresh()
  })

  export function reload() {
    return refresh()
  }
</script>

<section class="ces-live" aria-label="AI CES live">
  <header class="ces-live__head">
    <h2 class="ces-live__title">AI CES Live</h2>
    <p class="ces-live__hint">15m · mutex с Rust · 5m независимый · один PM-кошелёк · без OTP</p>
  </header>

  {#if loading}
    <p class="pm-empty">Статус…</p>
  {:else if !markets.length}
    <p class="pm-empty">Нет CES live</p>
  {:else}
    <div class="ces-live__row">
      {#each markets as m (m.market_key)}
        <div
          class="ces-live__cell"
          class:is-off={!m.enabled_wanted}
          data-market={m.market_key}
        >
          <div class="ces-live__meta">
            <span class="ces-live__asset">{m.label_ru || m.market_key}</span>
            <span class="ces-live__eq">{equityTxt(m)}</span>
            <span class="ces-live__tick">{m.last_tick_event || m.last_tick?.event || '—'}</span>
          </div>
          <label class="ces-live__switch" title={m.enabled_wanted ? 'Выключить CES' : 'Включить CES'}>
            <input
              type="checkbox"
              role="switch"
              aria-label={`${m.label_ru || m.market_key} ON/OFF`}
              checked={!!m.enabled_wanted}
              disabled={!!busy}
              on:change|preventDefault={() => onToggle(m.market_key, !m.enabled_wanted)}
            />
            <span class="ces-live__ui" aria-hidden="true"></span>
            <span class="ces-live__txt">{m.enabled_wanted ? 'вкл' : 'выкл'}</span>
          </label>
        </div>
      {/each}
    </div>
  {/if}

  {#if mutexBanner}
    <p class="ces-live__mutex" role="status">{mutexBanner}</p>
  {/if}
  {#if msg}
    <p class="ces-live__msg">{msg}</p>
  {/if}
  {#if err}
    <p class="pm-error">{OPS_ERROR_RU[err] || err}</p>
  {/if}
</section>

<style>
  .ces-live {
    margin: 0 0 1rem;
  }
  .ces-live__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.65rem;
  }
  .ces-live__title {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .ces-live__hint {
    margin: 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .ces-live__row {
    display: grid;
    gap: 0.55rem 0.75rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    .ces-live__row {
      grid-template-columns: 1fr;
    }
  }
  .ces-live__cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.55rem 0.7rem;
    border-radius: 10px;
    border: 1px solid var(--pm-line);
    background: var(--pm-elevated-bg, transparent);
  }
  .ces-live__cell.is-off {
    opacity: 0.72;
  }
  .ces-live__meta {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    min-width: 0;
  }
  .ces-live__asset {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--pm-ink);
  }
  .ces-live__eq {
    font-size: 0.78rem;
    font-variant-numeric: tabular-nums;
    color: var(--pm-ink);
  }
  .ces-live__tick {
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ces-live__switch {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    user-select: none;
  }
  .ces-live__switch input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
  .ces-live__ui {
    width: 1.7rem;
    height: 0.95rem;
    border-radius: 999px;
    background: rgba(127, 127, 127, 0.35);
    position: relative;
    flex: 0 0 auto;
  }
  .ces-live__ui::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s ease;
  }
  .ces-live__switch input:checked + .ces-live__ui {
    background: var(--pm-ok, #1a7f4e);
  }
  .ces-live__switch input:checked + .ces-live__ui::after {
    transform: translateX(0.72rem);
  }
  .ces-live__txt {
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
    min-width: 2.6rem;
  }
  .ces-live__msg {
    margin: 0.5rem 0 0;
    font-size: 0.75rem;
    color: var(--pm-ink-soft);
  }
  .ces-live__mutex {
    margin: 0.5rem 0 0;
    font-size: 0.78rem;
    line-height: 1.35;
    color: var(--pm-warn, #b8860b);
  }
</style>
